import { Box, Icon, Spacer, Text } from '@jfs/ui-new'
import { useIsFocused, useNavigation } from '@react-navigation/core'
import { FC, useEffect, useRef, useState } from 'react'
import Lottie from 'react-lottie'
import { useWindowDimensions, View, ViewStyle } from 'react-native'
import * as Scanner from '../../../assets/lottie/scanner.json'
import { BottomSheet } from '../../../comps/BottomSheet'
import { ModBox } from '../../../comps/ModifiedBox/ModifiedBox'
import { QrScanner } from '../../../comps/QrScanner'
import { useStores } from '../../../data/store'
import { Routes } from '../../../nav/routes'
import { isSessionValid } from '../../../util/util'

const ScanQrCode: FC = (props: any) => {
  const { route } = props
  const lottieRef = useRef<any>()

  const { resetAccessToken, initialized } = useStores((store) => ({
    resetAccessToken: store.agentQrSession.resetAccessToken,
    initialized: store.agentQrSession.initialized,
  }))

  const isLandscape = () => window.matchMedia('(orientation:landscape)').matches
  const navigation = useNavigation()
  const [showInvalidQR, setInvalidQR] = useState(false)
  const [showPermissionDenied, setShowPermissionDenied] = useState(false)
  const [isScanComplete, setScanComplete] = useState(false)
  const isFocused = useIsFocused()

  useEffect(() => {
    if (!initialized && isFocused) {
      if (navigation?.canGoBack()) {
        navigation.goBack()
      } else {
        const csoSessData = window.localStorage.getItem('csoSessData')
        console.log('csoSessData', csoSessData)
        if (csoSessData && csoSessData?.length > 0 && isSessionValid(csoSessData)) {
          navigation.reset({
            index: 0,
            routes: [{ name: Routes.midslist }],
          })
        } else {
          navigation.reset({
            index: 0,
            routes: [{ name: Routes.QrAgentLogin }],
          })
          resetAccessToken()
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFocused, navigation, resetAccessToken])

  useEffect(() => {
    if (isFocused) {
      lottieRef.current?.play()
    }
  }, [isFocused])

  const data = route?.params?.data ?? undefined
  const { width: SCREEN_WIDTH, height: SCREEN_HEIGHT } = useWindowDimensions()
  const scannerLottie = {
    loop: true,
    autoplay: true,
    animationData: Scanner,
    rendererSettings: {
      preserveAspectRatio: 'xMidYMid meet',
    },
  }
  const mStyles = styles(SCREEN_WIDTH, SCREEN_HEIGHT)

  const extractVpa = (str?: string) => {
    if (str && str.startsWith('upi://')) {
      const initialIndex = str.indexOf('pa=')
      if (initialIndex === -1 && initialIndex < str.length - 3) {
        return ''
      }
      let lastIndex = str.indexOf('&', initialIndex)
      lastIndex = lastIndex === -1 ? str.length : lastIndex
      const vpa = str.substring(initialIndex + 3, lastIndex)
      const valid = /^[\w\.\-_]{3,}@[a-zA-Z]{3,}/.test(vpa)
      if (valid) {
        return vpa
      }
    }
    return ''
  }

  const onScan = (result: any) => {
    if (!!result && !isScanComplete) {
      const vpa = extractVpa(result.text)
      if (vpa) {
        console.log('vpa', vpa)
        const reviewData = {
          mid: data?.id ?? '',
          name: data?.name ?? '',
          mobile: data?.mobile ?? '',
          vpa: vpa ?? '',
          tid: data?.tid ?? '',
          tr: extractTr(result.text) ?? '',
        }
        setScanComplete(true)
        navigation.reset({
          index: 0,
          routes: [
            {
              name: 'review',
              params: reviewData,
            },
          ],
        })
      } else {
        setInvalidQR(true)
      }
    }
  }

  const extractTr = (str?: string) => {
    if (str && str.startsWith('upi://')) {
      const initialIndex = str.indexOf('tr=')
      if (initialIndex === -1 && initialIndex < str.length - 3) {
        return ''
      }
      let lastIndex = str.indexOf('&', initialIndex)
      lastIndex = lastIndex === -1 ? str.length : lastIndex
      const tr = str.substring(initialIndex + 3, lastIndex)
      return tr
    }
    return ''
  }

  const onError = (error: any) => {
    console.log('QR Error', error)
    if (error) {
      if (error.name === 'NotAllowedError' && error.message === 'Permission denied') {
        setShowPermissionDenied(true)
      }
    }
  }

  const onClose = () => {
    navigation.goBack()
  }

  const onCloseBottomSheet = () => {
    setInvalidQR(false)
    setShowPermissionDenied(false)
  }

  return (
    <View style={{ flex: 1 }}>
      <QrScanner onScan={onScan} onError={onError} />
      <View style={{ ...mStyles.holeStyle }} />
      <ModBox position={'absolute'} top={0} left={0}>
        <Box
          testID="topLeft"
          w={7}
          h={7}
          borderRadius={3.5}
          bg={'WHITE'}
          position={'absolute'}
          left={SCREEN_WIDTH / 2 - (isLandscape() ? 143 : 153)}
          top={SCREEN_HEIGHT / 2 - (isLandscape() ? 148 : 158)}
        />
        <Box
          testID="bottomLeft"
          w={7}
          h={7}
          borderRadius={3.5}
          bg={'WHITE'}
          position={'absolute'}
          left={SCREEN_WIDTH / 2 - (isLandscape() ? 143 : 153)}
          top={SCREEN_HEIGHT / 2 + (isLandscape() ? 138 : 148)}
        />
        <Box
          testID="topRight"
          w={7}
          h={7}
          borderRadius={3.5}
          bg={'WHITE'}
          position={'absolute'}
          left={SCREEN_WIDTH / 2 + (isLandscape() ? 138 : 148)}
          top={SCREEN_HEIGHT / 2 - (isLandscape() ? 148 : 158)}
        />
        <Box
          testID="bottomRight"
          w={7}
          h={7}
          borderRadius={3.5}
          bg={'WHITE'}
          position={'absolute'}
          left={SCREEN_WIDTH / 2 + (isLandscape() ? 138 : 148)}
          top={SCREEN_HEIGHT / 2 + (isLandscape() ? 138 : 148)}
        />
        <ModBox
          width={286}
          height={256}
          alignItems={'center'}
          justifyContent={'center'}
          position={'absolute'}
          left={SCREEN_WIDTH / 2 - (isLandscape() ? 143 : 143)}
          top={SCREEN_HEIGHT / 2 - (isLandscape() ? 148 : 128)}>
          <Lottie ref={lottieRef} options={scannerLottie} />
        </ModBox>
        <Box h={30} flexDirection={'row'} position={'absolute'} top={32} left={32} zIndex={1000000}>
          <Box onPress={onClose} testID="close-qr" marginRight={16} underlayColor={'TRANSPARENT'}>
            <Icon color="WHITE" size={20} id="CLOSE" />
          </Box>
          <Text
            fontFamily="jiotype-medium"
            fontSize={16}
            title={'Scan QR to Activate'}
            color={'WHITE'}
            numberOfLines={1}
          />
        </Box>
        <ModBox
          w={SCREEN_WIDTH}
          h={70}
          position={'absolute'}
          top={isLandscape() ? SCREEN_HEIGHT / 2 : SCREEN_HEIGHT / 2 + 256}
          left={isLandscape() ? -32 : undefined}
          alignItems={isLandscape() ? 'flex-end' : 'center'}
          justifyContent={isLandscape() ? 'center' : 'flex-end'}>
          <Text fontFamily="jiotype-bold" fontSize={14} title={data?.name ?? 'N/A'} color={'WHITE'} />
          <Spacer direction={'vertical'} size={8} />
          <Text fontFamily="jiotype-medium" fontSize={12} title={`MID: ${data?.id ?? 'N/A'}`} color={'WHITE'} />
          <Box direction={'row'} alignItems={'center'} marginTop={16} marginBottom={16}>
            <Icon color="WHITE" size={20} id="MOBILE" />
            <Text fontFamily="jiotype-bold" fontSize={14} title={`+91 ${data?.mobile ?? 'N/A'}`} color={'WHITE'} />
          </Box>
        </ModBox>
        <ModBox position="absolute" bottom={0} w={'100%'} show={showInvalidQR || showPermissionDenied}>
          <BottomSheet
            status={showInvalidQR ? 'failed' : showPermissionDenied ? 'permissiondenied' : ''}
            onClose={onCloseBottomSheet}
          />
        </ModBox>
      </ModBox>
    </View>
  )
}

export { ScanQrCode as default }

const styles = (width: number, height: number) => {
  return {
    holeStyle: {
      position: 'absolute',
      alignItems: 'center',
      justifyContent: 'center',
      left: width / 2 - 128,
      top: height / 2 - 128,
      width: 256,
      height: 256,
      boxShadow: `0px 0px 0px ${height}px rgba(0, 0, 0, .8)`,
      borderRadius: 8,
    } as ViewStyle,
  }
}
